import { useState } from "react";
import { X, VideoCamera, CheckCircle, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import ModalPortal from "@/components/ModalPortal";

const API = process.env.REACT_APP_BACKEND_URL;

export default function DemoBookingModal({ open, onClose }) {
  const [company, setCompany] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [website, setWebsite] = useState(""); // honeypot — döljs
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  if (!open) return null;

  const reset = () => {
    setCompany("");
    setName("");
    setPhone("");
    setEmail("");
    setWebsite("");
    setSent(false);
  };

  const handleClose = () => {
    if (submitting) return;
    reset();
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!company.trim() || !name.trim() || !phone.trim() || !email.trim()) {
      toast.error("Fyll i alla fält");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/demo/request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          company: company.trim(),
          name: name.trim(),
          phone: phone.trim(),
          email: email.trim(),
          website: website || null,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Något gick fel");
      }
      setSent(true);
    } catch (err) {
      toast.error(err.message || "Kunde inte skicka. Försök igen om en stund.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalPortal>
      <div
        className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
        data-testid="demo-booking-modal"
        onClick={handleClose}
      >
        <div
          className="bg-surface rounded-2xl max-w-lg w-full border border-app shadow-2xl overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header med gradient */}
          <div
            className="relative text-white p-6 sm:p-8"
            style={{ background: "linear-gradient(135deg, #c25934 0%, #a04620 100%)" }}
          >
            <button
              onClick={handleClose}
              className="absolute top-4 right-4 text-white/70 hover:text-white transition-colors"
              data-testid="demo-modal-close"
              aria-label="Stäng"
            >
              <X size={22} />
            </button>
            <div className="flex items-center gap-3 mb-3">
              <div className="bg-white/15 rounded-lg p-2">
                <VideoCamera size={22} weight="fill" />
              </div>
              <span className="text-xs tracking-widest uppercase opacity-80">
                Live demo
              </span>
            </div>
            <h2 className="font-heading text-2xl sm:text-3xl leading-tight">
              {sent ? "Tack, vi ringer snart!" : "Boka en personlig genomgång"}
            </h2>
            {!sent && (
              <p className="text-sm sm:text-base mt-2 opacity-90">
                Visa upp Tidsrapporten för hela ditt team på 20 minuter via Google Meet — helt kostnadsfritt.
              </p>
            )}
          </div>

          {/* Body */}
          <div className="p-6 sm:p-8">
            {sent ? (
              <div className="text-center space-y-4" data-testid="demo-modal-success">
                <div className="mx-auto w-16 h-16 rounded-full bg-status-active/10 flex items-center justify-center">
                  <CheckCircle size={40} weight="fill" className="text-status-active" />
                </div>
                <p className="text-text-primary">
                  Din förfrågan är skickad till <strong>Paul</strong>. Vi ringer upp på{" "}
                  <strong>{phone}</strong> inom 24 timmar för att bestämma en tid som passar dig.
                </p>
                <p className="text-sm text-text-muted">
                  Håll utkik efter samtal från oss — vi ringer under vardagar 8–17.
                </p>
                <button
                  onClick={handleClose}
                  className="mt-2 bg-brand hover:bg-brand-hover text-white px-6 py-3 rounded-md font-medium inline-flex items-center gap-2 transition-colors"
                  data-testid="demo-modal-done"
                >
                  Klart
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Honeypot — helt dolt för människor */}
                <input
                  type="text"
                  name="website"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  style={{
                    position: "absolute",
                    left: "-9999px",
                    height: 0,
                    width: 0,
                    opacity: 0,
                    pointerEvents: "none",
                  }}
                />
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-1.5">
                    Företagsnamn
                  </label>
                  <input
                    type="text"
                    value={company}
                    onChange={(e) => setCompany(e.target.value)}
                    required
                    autoFocus
                    className="w-full px-4 py-2.5 rounded-md border border-app bg-surface focus:outline-none focus:ring-2 focus:ring-brand/40 focus:border-brand"
                    placeholder="Trappbolaget AB"
                    data-testid="demo-input-company"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-1.5">
                    Ditt namn
                  </label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    className="w-full px-4 py-2.5 rounded-md border border-app bg-surface focus:outline-none focus:ring-2 focus:ring-brand/40 focus:border-brand"
                    placeholder="Anna Andersson"
                    data-testid="demo-input-name"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-1.5">
                      Telefon
                    </label>
                    <input
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      required
                      className="w-full px-4 py-2.5 rounded-md border border-app bg-surface focus:outline-none focus:ring-2 focus:ring-brand/40 focus:border-brand"
                      placeholder="070-123 45 67"
                      data-testid="demo-input-phone"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-1.5">
                      E-post
                    </label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="w-full px-4 py-2.5 rounded-md border border-app bg-surface focus:outline-none focus:ring-2 focus:ring-brand/40 focus:border-brand"
                      placeholder="anna@foretag.se"
                      data-testid="demo-input-email"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-brand hover:bg-brand-hover disabled:opacity-60 disabled:cursor-not-allowed text-white px-6 py-3.5 rounded-md font-medium flex items-center justify-center gap-2 transition-colors"
                  data-testid="demo-submit-button"
                >
                  {submitting ? "Skickar…" : "Skicka förfrågan"}
                  {!submitting && <ArrowRight size={18} weight="bold" />}
                </button>
                <p className="text-xs text-text-muted text-center">
                  Vi kontaktar dig inom 24 timmar för att boka en tid som passar.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </ModalPortal>
  );
}
